import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { faPencil, faRotateReverse } from "@fortawesome/pro-duotone-svg-icons";
import {
  faFileInvoiceDollar,
  faFiles,
} from "@fortawesome/pro-regular-svg-icons";
import { faFunnelDollar } from "@fortawesome/pro-solid-svg-icons";
import { Box } from "@material-ui/core";
import { useFormikContext } from "formik";
import { kebabCase } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import {
  BillingDocumentAction,
  BillingRunAction,
  PaymentAction,
  SaleAction,
  setSetting,
} from "actions";

import { IntegrationCredentialAction } from "actions/integrationCredentials";
import { openGenerateReportsFromBusinessTransactionsTable } from "actions/modals/business";

import AgGridTable, {
  globalDefaultColDef,
} from "components/AgGrid/AgGridContainer";
import { LoadAll } from "components/AgGrid/LoadAll";
import BulkUpdateBillingDocumentModal from "components/BulkUpdateOptionalFieldsModal/BillingDocuments";
import { Column } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { useGetDownloadOutstandingSettlementReport } from "components/MoneyTable/outstandingSettlementReport";
import { GenerateMode } from "components/MultiSaleReports";
import { TitleText } from "components/Text";
import { TimeSince } from "components/TimeSince";

import { AgGridTables } from "constants/aggrid";
import { IntegrationTypes } from "constants/integrations";
import { ApiModel } from "constants/loading";
import { ModalTypes } from "constants/navigation";
import { DeploymentPermissions } from "constants/permissions";
import { Settings } from "constants/settings";

import { firstIfAllEqual } from "lib";

import { EmailDetailGridOptions } from "lib/agGrid/columnDefinitions/email";
import { openModalLink } from "lib/navigation";
import toast from "lib/toast";

import {
  getDefaultBillingSettings,
  getActiveRole,
  getBillingDocuments,
  getIntegrationCredentialById,
  getIntegrationCredentialIdsByType,
  getIsFetchingBillingDocuments,
  getProperties,
  getSetting,
} from "selectors";

import { useBoolean, useHasDeploymentPermission, useMountEffect } from "hooks";
import { takeFormikSnapshot } from "sagas/modals/util";

import { MoneyColumnDefinitions } from "./columnDefinitions";
import { useGetBillingDocumentRowDataByBillingRunOrBusinessId } from "hooks/useGetBillingDocumentRowDataByBillingRunOrBusinessId";

const getRowId = params => params.data.billingDocument.id;
const rowSelectionId = "billingDocument.id";

const detailCellRendererParams = {
  detailGridOptions: EmailDetailGridOptions,
  getDetailRowData: params => {
    params.successCallback(params.data.emails);
  },
};

const defaultColDef = {
  ...globalDefaultColDef,
  enableCellChangeFlash: false,
};

export const MoneyTable = ({
  businessId,
  allDocumentsLoaded,
  setAllDocumentsLoaded,
  tableName = AgGridTables.MONEY,
}) => {
  const [selectedBillingDocumentIds, setSelectedBillingDocumentIds] = useState(
    [],
  );
  const propertyByIdLookup = useSelector(getProperties);
  const userRoleSlug = useSelector(state => getActiveRole(state).slug);
  const isDocumentsFetching = useSelector(getIsFetchingBillingDocuments);

  const billingDocumentByIdLookup = useSelector(getBillingDocuments);

  const rowData = useGetBillingDocumentRowDataByBillingRunOrBusinessId(
    null,
    businessId,
  );

  const formikProps = useFormikContext();

  const context = {
    propertyByIdLookup,
    userRoleSlug,
  };

  const dispatch = useDispatch();

  const xeroCredentialIds = useSelector(
    getIntegrationCredentialIdsByType(IntegrationTypes.Xero),
  );

  const xeroCredentialId = xeroCredentialIds?.[0];

  const integrationCredential = useSelector(
    getIntegrationCredentialById(xeroCredentialId),
  );

  const lastSync = useMemo(
    () =>
      integrationCredential ? (
        <div>
          <i>
            Integration synced:{" "}
            {integrationCredential.integrationDocumentsLastSyncedAt
              ? TimeSince({
                  utcDateTimeStr:
                    integrationCredential.integrationDocumentsLastSyncedAt,
                })
              : "never"}
          </i>
        </div>
      ) : null,
    [integrationCredential],
  );

  const syncXeroDocuments = useCallback(
    () => dispatch(IntegrationCredentialAction.syncDocuments(xeroCredentialId)),
    [dispatch, xeroCredentialId],
  );

  const syncingDocumentToastText = "Fetching All Documents";

  const loadAllDocuments = useCallback(() => {
    dispatch(BillingDocumentAction.request());
    dispatch(PaymentAction.request());
    toast.syncing(syncingDocumentToastText, {
      autoClose: false,
    });
    setAllDocumentsLoaded(true);
  }, [dispatch, setAllDocumentsLoaded]);

  useEffect(() => {
    if (!isDocumentsFetching) {
      toast.dismiss(kebabCase(syncingDocumentToastText));
    }
  }, [isDocumentsFetching]);

  const loadAllLabel = useMemo(() => {
    if (businessId) {
      return null;
    }
    return (
      <LoadAll
        allLoaded={allDocumentsLoaded}
        onClick={loadAllDocuments}
        text="Only unpaid documents and those modified within 45 days are shown."
      />
    );
  }, [businessId, allDocumentsLoaded, loadAllDocuments]);

  const [
    isEditBillingDocumentsOpen,
    openEditBillingDocuments,
    closeEditBillingDocuments,
  ] = useBoolean(false);

  const selectedBillingDocuments = useMemo(
    () =>
      selectedBillingDocumentIds.map(
        billingDocumentId => billingDocumentByIdLookup[billingDocumentId],
      ),

    [billingDocumentByIdLookup, selectedBillingDocumentIds],
  );

  const bulkInitialValues = useMemo(
    () => ({
      status: firstIfAllEqual(
        selectedBillingDocuments.map(billingDocument => billingDocument.status),
        undefined,
      ),
      dueDate: firstIfAllEqual(
        selectedBillingDocuments.map(
          billingDocument => billingDocument.dueDate,
        ),
        undefined,
      ),
    }),
    [selectedBillingDocuments],
  );
  const selectedCount = selectedBillingDocumentIds.length;

  const [onGridReadyExtra, handleDownloadOutstandingSettlementReport] =
    useGetDownloadOutstandingSettlementReport();

  const agGridRef = useRef();

  const generateInterestCharges = () => {
    openModalLink(ModalTypes.Interest);
  };

  const onClickGenerateFinancialStatements = () => {
    openModalLink(ModalTypes.MultiSaleReports, {
      allowedReports: [
        "FinancialStatementReport",
        "FinancialStatementZipReport",
        "DetailedFinancialStatementReport",
      ],
      generateMode: GenerateMode.MANY,
    });
  };

  const onClickGenerateBusinessReports = useCallback(() => {
    dispatch(
      openGenerateReportsFromBusinessTransactionsTable(
        businessId,
        takeFormikSnapshot(formikProps),
      ),
    );
  }, [businessId, dispatch, formikProps]);

  function onGridReady(agGrid) {
    agGridRef.current = agGrid;
    onGridReadyExtra(agGrid);
  }

  const hasInterestFeature = useHasDeploymentPermission(
    DeploymentPermissions.featureInterest,
  );

  const billingSettings = useSelector(getDefaultBillingSettings);
  const interestEnabled =
    !businessId &&
    hasInterestFeature &&
    billingSettings &&
    billingSettings.enableInterest;

  const additionalActions = useMemo(
    () =>
      [
        !businessId && {
          title: "Download Outstanding Settlement Report",
          icon: faFunnelDollar,
          onClick: handleDownloadOutstandingSettlementReport,
          dataTour: "outstandingSettlementReport",
        },
        integrationCredential && {
          title: "Sync Financial Integrations",
          icon: faRotateReverse,
          onClick: syncXeroDocuments,
          dataTour: "syncFinancialIntegrations",
          ref: "syncFinancialIntegrations",
        },
        interestEnabled && {
          title: "Generate Interest Charges",
          icon: faFileInvoiceDollar,
          onClick: generateInterestCharges,
          dataTour: "generateInterestCharges",
        },
        {
          title: `Bulk Edit (${selectedCount})`,
          icon: faPencil,
          isDisabled: selectedCount === 0,
          onClick: openEditBillingDocuments,
          dataTour: "bulkUpdate",
        },
        {
          title: "Generate Financial Reports",
          icon: faFiles,
          onClick:
            businessId && Boolean(formikProps)
              ? onClickGenerateBusinessReports
              : onClickGenerateFinancialStatements,
          dataTour: "generateFinancialStatements",
        },
      ].filter(Boolean),
    [
      businessId,
      formikProps,
      handleDownloadOutstandingSettlementReport,
      integrationCredential,
      interestEnabled,
      onClickGenerateBusinessReports,
      openEditBillingDocuments,
      selectedCount,
      syncXeroDocuments,
    ],
  );

  const onRowSelectionChange = rows => {
    // This logic is already performed in the AgGridTable component :/
    setSelectedBillingDocumentIds(rows.map(r => r.billingDocument.id));
  };

  const columnDefs = useMemo(
    () => MoneyColumnDefinitions(interestEnabled),
    [interestEnabled],
  );

  const defaultCsvExportParams = useMemo(
    () => ({
      // only export selected rows to the export csv - if no rows are selected export all
      onlySelected: !!selectedBillingDocumentIds.length,
    }),
    [selectedBillingDocumentIds.length],
  );

  const extraHeaderComponents = useMemo(
    () => [loadAllLabel, lastSync],
    [lastSync, loadAllLabel],
  );

  return (
    <Column fullHeight>
      {!businessId && (
        <Box p={2}>
          <TitleText>Money</TitleText>
        </Box>
      )}

      <AgGridTable
        defaultColDef={defaultColDef}
        rowSelectionId={rowSelectionId}
        columnDefs={columnDefs}
        context={context}
        extraActionComponents={extraHeaderComponents}
        getRowId={getRowId}
        rowData={rowData}
        additionalActions={additionalActions}
        onGridReady={onGridReady}
        tableName={tableName}
        detailCellRendererParams={detailCellRendererParams}
        onRowSelectionChanged={onRowSelectionChange}
        rowSelection="multiple"
        masterDetail
        multiSelectActionOnly
        suppressRowClickSelection
        defaultCsvExportParams={defaultCsvExportParams}
        rowsSelectable
      />
      {isEditBillingDocumentsOpen && (
        <BulkUpdateBillingDocumentModal
          onClose={closeEditBillingDocuments}
          billingDocumentIds={selectedBillingDocumentIds}
          initialValues={bulkInitialValues}
        />
      )}
    </Column>
  );
};

export const MoneyTableWrapper = ({ businessId, tableName }) => {
  const billingRunsExist = useSelector(state => {
    const billingRuns = getBillingDocuments(state);
    return Object.keys(billingRuns).length > 0;
  });

  const forceNotLoading = !businessId && billingRunsExist;

  const [allDocumentsLoaded, setAllDocumentsLoaded] = useState(false);

  const dispatch = useDispatch();

  const allSalesLoaded = useSelector(getSetting(Settings.allSalesLoaded));
  // We only want to fetch these once on mount, and outside of the <WaitFor> block to avoid rerenders forcing re-gets.
  useMountEffect(() => {
    if (businessId) {
      dispatch(
        BillingDocumentAction.request({
          extraQueryParams: {
            business__cbid: businessId,
          },
        }),
      );
    } else {
      dispatch(BillingRunAction.enable());
      dispatch(
        BillingDocumentAction.request({
          extraQueryParams: { recent_or_overdue: true },
        }),
      );
    }
    dispatch(PaymentAction.request({ onRecentOrOverdue: true }));
    if (!allSalesLoaded) {
      dispatch(setSetting(Settings.allSalesLoaded, true));
      dispatch(SaleAction.request({ fetchAll: true }));
    }
    return () => {
      dispatch(BillingRunAction.disable());
    };
  });

  return (
    <WaitForSync
      requiredData={[
        ApiModel.BILLING_DOCUMENTS,
        ApiModel.INTEGRATION_CREDENTIALS,
        ApiModel.SALES,
        ApiModel.PAYMENTS,
      ]}
      forceNotLoading={forceNotLoading}
    >
      <MoneyTable
        allDocumentsLoaded={allDocumentsLoaded}
        setAllDocumentsLoaded={setAllDocumentsLoaded}
        businessId={businessId}
        tableName={tableName}
      />
    </WaitForSync>
  );
};
